import { darken, transparentize } from 'polished'

import { themeHelpers } from '@subaio/utils'
import { defaultButtonTheme, defaultInputTheme, defaultSpacing } from '@subaio/webapp'

import { getBrandTheme } from './brandThemes'

const animation = {
	default: 'cubic-bezier(0.4, 0, 0.2, 1)',
}

const theme = (brandId?: string) => {
	const colors = {
		actionOnLight: '#2075CF',
		actionOnDark: '#2075CF',
		btnBackOnDark: '#62cbf8',
		btnTextOnDark: '#ffffff',
		positive: '#39843C',
		negative: '#B00020',
		background: '#F8F9FA',
		navBorders: '#eae9e9',
		hero: '#272E3D',
		alert: '#B00020',
		warning: '#22467C',
		headlines: '#000000',
		text: '#000000DE',
		amountPositive: '#000000DE',
		amountNegative: '#B00020',
		labelOnLight: 'rgba(0, 0, 0, 1)',
		labelOnDark: 'rgba(255, 255, 255, 1)',
		disabledText: '#00000061',
		oakPositive: '#272e3c',
		oakNegative: '#dd7169',
		chart: 'rgb(119, 138, 131)',
		heroChart: 'rgb(255, 255, 255)',
		chartBackground: 'rgb(255, 255, 255)',
		heroChartBackground: 'rgb(39, 46, 61)',

		black: '#000',
		white: '#FFF',

		...getBrandTheme(brandId).colors,
	}

	const fontFamily = getBrandTheme(brandId).fontFamily ?? `'Roboto', sans-serif`

	const font = {
		family: {
			header: fontFamily,
			base: fontFamily,
		},
	}

	const fonts = {
		headline: {
			largest: `500 34px/100% ${fontFamily}`,
			large: `500 24px/100% ${fontFamily}`,
			medium: `500 20px/100% ${fontFamily}`,
			small: `600 14px/100% ${fontFamily}`,
		},
		subtitle: {
			large: `600 16px/100% ${fontFamily}`,
			medium: `400 14px/100% ${fontFamily}`,
			small: `400 12px/100% ${fontFamily}`,
		},
		body: {
			large: `500 16px/100% ${fontFamily}`,
			medium: `400 14px/100% ${fontFamily}`,
		},
		input: {
			label: `400 11px/100% ${fontFamily}`,
			content: `400 16px/100% ${fontFamily}`,
			hint: `400 12px/100% ${fontFamily}`,
		},
		button: `500 14px/100% ${fontFamily}`,
	}

	return {
		color: {
			cardBackground: colors.white,
			cardShadow: transparentize(0.88, colors.black),
			cardSubsectionBackground: '#f5f5f5',

			actionLabel: colors.white,
			action: colors.actionOnLight,
			actionBorder: transparentize(0.88, colors.black),
			actionHover: darken(0.1, colors.actionOnLight),
			actionDisabled: transparentize(0.88, colors.black),
			actionDisabledLabel: colors.disabledText,
			link: colors.actionOnLight,

			numberPositive: colors.amountPositive,
			numberNegative: colors.amountNegative,

			iconBackground: colors.hero,

			headerIconPrimary: colors.hero,
			headerIconPrimaryAccent: colors.actionOnLight,
			headerIconBackground: colors.white,
			headerIconGradientStart: colors.white,
			headerIconGradientStop: transparentize(0.8, colors.hero),

			warning: colors.warning,

			text: colors.text,
			textMuted: transparentize(0.4, colors.text),
			textContrast: colors.white,
			border: colors.navBorders,
			background: colors.background,
			separator: '#eae9e9',
			scrollbarThumb: '#ADB1B2',

			input: {
				...defaultInputTheme,
				label: colors.text,

				focusOutline: colors.actionOnLight,

				background: colors.white,
				backgroundHover: colors.white,
				backgroundReadOnly: colors.white,
				backgroundError: transparentize(0.91, colors.alert),
				backgroundErrorHover: transparentize(0.91, colors.alert),

				border: transparentize(0.88, colors.black),
				borderHover: colors.actionOnLight,
				borderError: colors.alert,
				borderOutlineHover: colors.actionOnLight,
				borderOutlineError: colors.alert,

				menuIndicator: transparentize(0.88, colors.black),
				menuIndicatorActive: colors.black,
				menuOptionCheckMark: colors.actionOnLight,

				menuOptionBackgroundHighlighted: transparentize(0.8, colors.actionOnLight),
				menuOptionBackgroundSelected: transparentize(0.9, colors.actionOnLight),
				menuOptionBackgroundHover: transparentize(0.8, colors.actionOnLight),
			},

			// Required because of the theme set in @subaio/components
			primary: colors.actionOnLight,
			danger: colors.alert,
			textHeader: colors.text,

			// Required because of the theme set in @subaio/webapp
			/** System status indicators */
			status: {
				operational: '#5EB870',
				minor: '#FCB900',
				major: '#F27641',
				critical: '#E15554',
			},

			button: defaultButtonTheme,
		},
		animation,
		fonts,
		font,
		spacing: defaultSpacing,
		borderRadius: getBrandTheme(brandId).styles?.borderRadius ?? '4px',
	}
}

type Props = {
	brandId?: string
}

export const getTheme = ({ brandId }: Props) => ({
	...theme(brandId),
})

export const helpers = themeHelpers(getTheme({}))

export type Theme = ReturnType<typeof getTheme>
